import { Card, CircularProgress, Typography } from '@mui/material';
import { Box } from '@mui/system';
import React, { useEffect, useMemo, useState } from 'react';
import { FaFolderOpen } from 'react-icons/fa';
import {
  MdDashboard,
  MdOutlineAttachMoney,
  MdOutlineKeyboardArrowRight,
  MdPendingActions,
  MdPermMedia,
  MdPhotoLibrary,
  MdSource,
} from 'react-icons/md';
import { Link, useMatch, useNavigate } from 'react-router-dom';
import api, { authHeader } from '../../../../services/API/apiConfig';
import { Header } from '../../../components/singleComponents/headers';
import { ConstructionBudgets } from './budgets/ConstructionBudgets';
import { ConstructionDashboard } from './dashboard/ConstructionDashboard';
import { ConstructionDocuments } from './documents/ConstructionDocuments';
import { ConstructionPayable } from './payables/ConstructionPayable';
import { ConstructionPhotos } from './photos/ConstructionPhotos';
import { ConstructionSchedules } from './schedules/ConstructionSchedules';

import NoPermissions from '../../../components/singleComponents/alerts/NoPermissions';
import { TabClient } from '../../../components/singleComponents/tabs/TabClient';
import {
  CardDetailsStyled,
  ConstructionDetailsContainerStyled,
  ContainerCardsStyled,
  ContainerStyled,
  GoBackContainerStyled,
  IconBackStyled,
  MobileContainerStyled,
  PayablesCardStyled,
  PaymentCardStyled,
  RootStyled,
} from './index.styles';

export function ConstructionDetails() {
  const navigate = useNavigate();
  const match = useMatch('/constructions-details/:id');
  const id = match ? match.params.id : null;
  const [permissions, setPermissions] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [construction, setConstruction] = useState();
  const [payables, setPayables] = useState([]);
  const [pending, setPending] = useState();
  const [paid, setPaid] = useState();
  const [folders, setFolders] = useState([]);
  const [images, setImages] = useState([]);
  const [paidPercent, setPaidPercent] = useState(0);
  const [pendingPercent, setPendingPercent] = useState(0);
  const [sumPayable, setSumPayable] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  function handleGoBack() {
    navigate(-1);
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue); // Atualiza a aba ativa
  };

  const headings = useMemo(
    () =>
      permissions &&
      [
        {
          title: 'Lançamentos',
          icon: <MdSource size={26} />,
          name: 'payables',
        },
        {
          title: 'Orçamentos',
          icon: <MdOutlineAttachMoney size={26} />,
          name: 'budgets',
        },
        {
          title: 'Cronograma',
          icon: <MdPendingActions size={26} />,
          name: 'schedules',
        },
        { title: 'Fotos', icon: <MdPermMedia size={26} />, name: 'pictures' },
        {
          title: 'Documentos',
          icon: <MdSource size={26} />,
          name: 'documents',
        },
        {
          title: 'Dashboard',
          icon: <MdDashboard size={26} />,
          name: 'dashboard',
        },
      ].filter(({ name }) => permissions.includes(name)),
    [permissions]
  );

  const panels = useMemo(
    () =>
      permissions &&
      [
        { value: 0, component: <ConstructionPayable />, name: 'payables' },
        { value: 1, component: <ConstructionBudgets />, name: 'budgets' },
        { value: 2, component: <ConstructionSchedules />, name: 'schedules' },
        { value: 3, component: <ConstructionPhotos />, name: 'pictures' },
        { value: 4, component: <ConstructionDocuments />, name: 'documents' },
        { value: 5, component: <ConstructionDashboard />, name: 'dashboard' },
      ]
        .filter(({ name }) => permissions.includes(name))
        .map((panel, index) => ({ ...panel, value: index })),
    [permissions]
  );

  useEffect(() => {
    api
      .get(`/constructions/${id}/payables`, {
        headers: authHeader(),
      })
      .then((res) => {
        const { data, sum } = res.data;
        setPayables(data);
        setSumPayable(sum);
      });
  }, [id]);

  useEffect(() => {
    let paidValue = 0;
    let pendingValue = 0;
    if (payables.length) {
      payables.forEach(({ status, total }) => {
        const value = Number(
          total
            .replace('R$', '')
            .replace(' ', '')
            .replace('.', '')
            .replace(',', '.')
        );
        if (status === 'Pendente') {
          pendingValue += value;
        } else if (status === 'Pago') {
          paidValue += value;
        }
      });
    }
    setPaidPercent((paidValue * 100) / sumPayable);
    setPendingPercent((pendingValue * 100) / sumPayable);
    setPaid(paidValue);
    setPending(pendingValue);
  }, [payables, paidPercent, pendingPercent, paid, pending, sumPayable]);

  useEffect(() => {
    setIsLoading(true);
    const getConstruction = async () => {
      api
        .get(`/constructions/${id}`, {
          headers: authHeader(),
        })
        .then((res) => {
          const { data } = res.data;
          setPermissions(data.users[0].permissions);
          setConstruction(data);
        })
        .finally(() => {
          setIsLoading(false);
        });
    };
    getConstruction();
  }, [id]);

  useEffect(() => {
    const getFolders = async () => {
      api
        .get(`constructions/${id}/document-folders`, {
          headers: authHeader(),
        })
        .then((res) => {
          const { data } = res.data;
          setFolders(data);
        });
    };
    getFolders();
  }, [id]);

  useEffect(() => {
    const getPictures = async () => {
      api
        .get(`/constructions/${id}/picture-folders`, {
          headers: authHeader(),
        })
        .then((res) => {
          const { data } = res.data;
          setImages(data);
        });
    };
    getPictures();
  }, [id]);

  if (isLoading) {
    return (
      <>
        <Header />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <CircularProgress />
        </Box>
      </>
    );
  }

  return (
    <>
      <Header />
      <ContainerStyled>
        <GoBackContainerStyled>
          <IconBackStyled size={32} onClick={() => handleGoBack()} />
        </GoBackContainerStyled>
        <Link to="/client">Início</Link>
        <MobileContainerStyled>
          <ConstructionDetailsContainerStyled>
            <Typography variant="h2">
              {construction && construction.name}
            </Typography>
            <Typography variant="body1">
              {construction && `${construction.address}, nº`}
              {construction && construction.addres_num
                ? construction.addres_num
                : ' - , '}
              {construction && `${construction.city} - `}
              {construction && construction.state}
            </Typography>
            <Typography variant="body2">
              {construction && construction.status}
            </Typography>
          </ConstructionDetailsContainerStyled>
          <ContainerCardsStyled>
            {permissions && permissions.includes('payables') && (
              <PaymentCardStyled to={`/constructions-details/${id}/payables`}>
                <Typography variant="body2" style={{ fontWeigth: 'bold' }}>
                  Lançamentos
                </Typography>
                <Card style={{ padding: 20, display: 'flex', gap: 24 }}>
                  {(pendingPercent !== 0 || paidPercent !== 0) && (
                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                        alignItems: 'flex-end',
                      }}
                    >
                      <div
                        style={{
                          width: 25,
                          height: paidPercent !== 0 ? paidPercent : 2,
                          backgroundColor: '#29D17D',
                          borderRadius: 16,
                        }}
                      />
                      <div
                        style={{
                          width: 25,
                          height: pendingPercent !== 0 ? pendingPercent : 2,
                          backgroundColor: '#FF4A56',
                          borderRadius: 16,
                        }}
                      />
                    </div>
                  )}
                  <PayablesCardStyled>
                    <Typography variant="body2" className="payment">
                      {'Pagamento realizado '}
                      <span className="paid">
                        {new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                        }).format(paid)}
                      </span>
                    </Typography>
                    <Typography variant="body2" className="payment">
                      {'Pagamento pendente '}
                      <span className="pending">
                        {new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                        }).format(pending)}
                      </span>
                    </Typography>
                  </PayablesCardStyled>
                </Card>
              </PaymentCardStyled>
            )}
            <div>
              {permissions && permissions.includes('budgets') && (
                <Link
                  style={{ textDecoration: 'none' }}
                  to={`/constructions-details/${id}/budgets`}
                >
                  <CardDetailsStyled>
                    <div className="leftCard">
                      <MdOutlineAttachMoney size={20} />
                      <Typography variant="body1">Orçamento</Typography>
                    </div>
                    <div className="rigthCard">
                      <MdOutlineKeyboardArrowRight size={20} />
                    </div>
                  </CardDetailsStyled>
                </Link>
              )}
              {permissions && permissions.includes('schedules') && (
                <Link
                  style={{ textDecoration: 'none' }}
                  to={`/constructions-details/${id}/schedules`}
                >
                  <CardDetailsStyled>
                    <div className="leftCard">
                      <MdPendingActions size={20} />
                      <Typography variant="body1">Cronograma</Typography>
                    </div>
                    <div className="rigthCard">
                      <MdOutlineKeyboardArrowRight size={20} />
                    </div>
                  </CardDetailsStyled>
                </Link>
              )}
              {permissions && permissions.includes('pictures') && (
                <Link
                  style={{ textDecoration: 'none' }}
                  to={`/constructions-details/${id}/photos`}
                >
                  <CardDetailsStyled>
                    <div className="leftCard">
                      <MdPhotoLibrary size={20} />
                      <Typography variant="body1">Fotos</Typography>
                    </div>
                    <div className="rigthCard">
                      <Typography variant="body1">
                        {images.length && images.length}
                      </Typography>
                      <MdOutlineKeyboardArrowRight size={20} />
                    </div>
                  </CardDetailsStyled>
                </Link>
              )}
              {permissions && permissions.includes('documents') && (
                <Link
                  style={{ textDecoration: 'none' }}
                  to={`/constructions-details/${id}/documents`}
                >
                  <CardDetailsStyled>
                    <div className="leftCard">
                      <FaFolderOpen size={20} />
                      <Typography variant="body1">Documentos</Typography>
                    </div>
                    <div className="rigthCard">
                      <Typography variant="body1">
                        {folders.length && folders.length}
                      </Typography>
                      <MdOutlineKeyboardArrowRight size={20} />
                    </div>
                  </CardDetailsStyled>
                </Link>
              )}
              {permissions && permissions.includes('dashboard') && (
                <Link
                  style={{ textDecoration: 'none' }}
                  to={`/constructions-details/${id}/dashboard`}
                >
                  <CardDetailsStyled>
                    <div className="leftCard">
                      <MdDashboard size={20} />
                      <Typography variant="body1">Dashboard</Typography>
                    </div>
                    <div className="rigthCard">
                      <MdOutlineKeyboardArrowRight size={20} />
                    </div>
                  </CardDetailsStyled>
                </Link>
              )}
            </div>
          </ContainerCardsStyled>
        </MobileContainerStyled>
        <RootStyled>
          {permissions ? (
            <TabClient
              headings={headings}
              panels={panels}
              activeTab={activeTab}
              onTabChange={handleTabChange}
            />
          ) : (
            <NoPermissions />
          )}
        </RootStyled>
      </ContainerStyled>
    </>
  );
}
